// Проверка картинок мобильного приложения до загрузки на сервер.
//
// Иконку и логотип шапки владелец грузит сам, и раньше уходило всё подряд:
// вертикальное фото вместо логотипа, скриншот вместо иконки, логотип на белой
// подложке. В приложении это видно сразу — плашка в шапке, мыльная иконка, белый
// квадрат на заставке. Поэтому плохой файл отсекаем здесь, с понятной причиной.
//
// Цифры — по ассетам брендов, которые уже собраны: иконка 1024×1024 (столько требует
// App Store). Заставки здесь нет: это файл бренда в шаблоне приложения, а не поле кабинета.

export const IMAGE_RULES = {
	// Иконка стора: квадрат, фон любой — его всё равно скругляет система
	logo: { square: true, minSide: 1024, transparent: false, trim: false },
	// Логотип шапки: горизонтальный, на прозрачном фоне. Высота в шапке 40, ширина
	// до 168 — длиннее 6:1 надпись станет нечитаемой, вертикальный не влезет вовсе
	header_logo: { minRatio: 1, maxRatio: 6, minHeight: 100, transparent: true, trim: true },
}

function loadImage(file) {
	return new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file)
		const img = new Image()
		img.onload = () => {
			URL.revokeObjectURL(url)
			resolve(img)
		}
		img.onerror = () => {
			URL.revokeObjectURL(url)
			reject(new Error('decode'))
		}
		img.src = url
	})
}

// Границы непрозрачной части. Почти прозрачные пиксели — сглаживание краёв, по ним не режем
function opaqueBounds(data, width, height) {
	let left = width, top = height, right = -1, bottom = -1
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			if (data[(y * width + x) * 4 + 3] < 16) continue
			if (x < left) left = x
			if (x > right) right = x
			if (y < top) top = y
			bottom = y
		}
	}
	return right < 0 ? null : { left, top, width: right - left + 1, height: bottom - top + 1 }
}

function canvasToFile(canvas, name) {
	return new Promise(resolve => {
		canvas.toBlob(blob => resolve(new File([blob], name.replace(/\.\w+$/, '') + '.png', { type: 'image/png' })), 'image/png')
	})
}

/**
 * Проверяет файл по правилу поля. Возвращает `{ file }` — что грузить (у логотипа
 * шапки уже без прозрачных полей), либо `{ error, params }` — ключ перевода причины.
 */
export async function checkImage(file, target) {
	const rule = IMAGE_RULES[target]
	if (!rule) return { file }

	if (rule.transparent && !['image/png', 'image/webp'].includes(file.type)) {
		return { error: 'image_error_need_png' }
	}

	let img
	try {
		img = await loadImage(file)
	} catch (e) {
		return { error: 'image_error_broken' }
	}

	const canvas = document.createElement('canvas')
	canvas.width = img.naturalWidth
	canvas.height = img.naturalHeight
	const ctx = canvas.getContext('2d')
	ctx.drawImage(img, 0, 0)
	const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)

	if (rule.transparent) {
		// Прозрачный фон — это прозрачные углы: PNG с альфа-каналом, но залитый белым, тоже не годится
		const w = canvas.width, h = canvas.height
		const corners = [0, w - 1, (h - 1) * w, h * w - 1]
		if (corners.some(index => data[index * 4 + 3] > 16)) {
			return { error: 'image_error_need_transparent' }
		}
	}

	let result = file
	let width = canvas.width
	let height = canvas.height

	if (rule.trim) {
		const bounds = opaqueBounds(data, canvas.width, canvas.height)
		if (!bounds) return { error: 'image_error_empty' }

		// Пустые поля съедают высоту в шапке: логотип выходил мелким. Обрезаем их сами
		if (bounds.width !== canvas.width || bounds.height !== canvas.height) {
			const trimmed = document.createElement('canvas')
			trimmed.width = bounds.width
			trimmed.height = bounds.height
			trimmed.getContext('2d').drawImage(canvas, bounds.left, bounds.top, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height)
			result = await canvasToFile(trimmed, file.name)
		}
		width = bounds.width
		height = bounds.height
	}

	if (rule.square && Math.abs(width / height - 1) > 0.02) {
		return { error: 'image_error_need_square', params: { width, height } }
	}
	if (rule.minSide && Math.min(width, height) < rule.minSide) {
		return { error: 'image_error_too_small', params: { width, height, min: `${rule.minSide}×${rule.minSide}` } }
	}

	const ratio = width / height
	if (rule.minRatio && ratio < rule.minRatio) {
		return { error: 'image_error_need_horizontal', params: { width, height } }
	}
	if (rule.maxRatio && ratio > rule.maxRatio) {
		return { error: 'image_error_too_wide', params: { width, height } }
	}
	if (rule.minHeight && height < rule.minHeight) {
		return { error: 'image_error_too_low', params: { width, height, min: rule.minHeight } }
	}

	return { file: result }
}
